import { ChevronDown, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { useStaffSearch } from '../../lib/options';
import { useDebounced } from '../../lib/useDebounced';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from './Spinner';

/**
 * A staff member is chosen by typing a name/employee code and picking from backend search
 * results — never by loading the whole roster into a <select>, which stops being usable
 * once a tenant has a few hundred staff.
 */
export function StaffPicker({ value, onChange, placeholder = 'Search by name or employee code…', clearLabel, className, required }: {
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  /** Shown as the top row of the results dropdown to clear the selection — omit to require a pick. */
  clearLabel?: string;
  className?: string;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useStaffSearch({ q: debouncedQuery || undefined, limit: 20 });
  const selected = useQuery({
    queryKey: ['staff', 'lookup', value],
    queryFn: async () => (await api.get<{ id: string; name: string; employeeCode: string }>(`/api/staff/${value}`)).data,
    enabled: !!value,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  function openSearch() {
    setQuery('');
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function pick(id: string) {
    onChange(id);
    setQuery('');
    setOpen(false);
  }

  return (
    <div className={`relative mt-1 ${className ?? ''}`} ref={containerRef}>
      {value && !open ? (
        <button type="button" className="field flex w-full items-center justify-between text-left" onClick={openSearch}>
          <span className="truncate">{selected.data ? `${selected.data.name} (${selected.data.employeeCode})` : <Spinner size="sm" />}</span>
          <div className="flex shrink-0 items-center gap-1">
            {clearLabel && (
              <span role="button" aria-label="Clear" className="rounded-full p-1 text-muted hover:bg-canvas hover:text-ink" onClick={(e) => { e.stopPropagation(); onChange(''); }}>
                <X size={14} />
              </span>
            )}
            <ChevronDown size={14} className="text-muted" />
          </div>
        </button>
      ) : (
        <div className="relative">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input
            ref={inputRef}
            className="field pl-9"
            placeholder={placeholder}
            required={required && !value}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setOpen(true)}
          />
        </div>
      )}

      {open && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-border bg-white shadow-lg">
          {clearLabel && (
            <button type="button" className="block w-full px-3 py-2 text-left text-sm font-medium text-muted hover:bg-canvas" onClick={() => pick('')}>{clearLabel}</button>
          )}
          {query.trim().length === 0 && <p className="px-3 py-2 text-sm text-muted">Type a name or employee code to search…</p>}
          {query.trim().length > 0 && results.isFetching && <p className="px-3 py-2 text-sm text-muted">Searching…</p>}
          {query.trim().length > 0 && !results.isFetching && results.data?.length === 0 && <p className="px-3 py-2 text-sm text-muted">No staff found.</p>}
          {results.data?.map((s) => (
            <button type="button" key={s.id} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas" onClick={() => pick(s.id)}>
              <span className="font-medium">{s.name}</span>{' '}
              <span className="text-muted">({s.employeeCode}{s.branchName ? ` • ${s.branchName}` : ''})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
